import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import { useActingClientAuthorizedCheck } from 'components/patient/useActingClientAuthorizedCheck';
import CircularProgress from 'components/shared/CircularProgress';
import { workflowQueryKeys } from 'services/api/workflows';

type Props = {
  // The route's own list page — where the user lands when no accessible client exists.
  fallbackPath: string;
  children: React.ReactNode;
};

// Scope the acting client to one that can access the workflow BEFORE the product gate
// runs — ProtectedRoute reads the acting client and would bounce a cross-client user to
// the default page before the show page ever mounts. Mirrors the patient/outbound
// referral deep-link behavior (OLIO-2960).
export default function WorkflowScopeGate({ fallbackPath, children }: Props) {
  const { workflowId } = useParams<{ workflowId: string }>();
  const queryClient = useQueryClient();

  // Lists cached under the previous acting client must not survive the switch.
  const handleClientSwitched = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: workflowQueryKeys.base });
  }, [queryClient]);

  const authorizationChecked = useActingClientAuthorizedCheck(workflowId, {
    resourceType: 'workflow',
    notFoundText: 'Referral not found',
    fallbackPath,
    onClientSwitched: handleClientSwitched,
  });

  if (!authorizationChecked) {
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  }

  return <>{children}</>;
}

const Centered = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;
